import React, { FC, useEffect, useMemo, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import DropdownMenu from "../../ui/DropdownMenu";
import { MoreVertical } from "lucide-react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { fetchAllCoupons } from "../../../redux/features/CouponSlice";
import AssignCouponModal from "./modals/AssignCouponModal";
import ClickableUserName from "../../../components/shared/ClickableUserName";
import {
  AssignedUser,
  CouponListItem,
} from "../../../redux/features/types/couponListTypes";
import CouponFilterBar, { prettifyCouponType } from "./CouponFilterBar";
import { useCouponFilters } from "./useCouponFilters";

interface CouponsTableProps {
  selectedCoupon: any;
  setSelectedCoupon: (value: any) => void;
  setEditModalOpen: (value: boolean) => void;
  /** OPP_ADMIN: hide Edit — assign unassigned coupons only */
  assignOnly?: boolean;
  /** Refetch list + summary cards after assign / other mutations from this table. */
  onCouponsMutated: () => void;
  /** Bumped by parent (e.g. after create) to force a refetch with current filters. */
  refreshKey?: number;
}

// Product rule is one buyer per coupon. Legacy multi-assign data (from before
// the rule existed) is collapsed to just the first buyer — no +N count.
// We surface the email under the name now that `search` matches emails too,
// so the admin can confirm "yes, this is the row I was looking for".
const renderAssignedUsers = (users: AssignedUser[] = []) => {
  if (!users || users.length === 0) {
    return <span className="text-gray-400">—</span>;
  }
  const first = users[0];
  return (
    <div className="flex flex-col min-w-0">
      <ClickableUserName user={first} userType="BUYER" />
      {first.email && (
        <span
          className="text-xs text-gray-500 truncate"
          title={first.email}
        >
          {first.email}
        </span>
      )}
    </div>
  );
};

const CouponsTable: FC<CouponsTableProps> = ({
  setSelectedCoupon,
  selectedCoupon,
  setEditModalOpen,
  assignOnly = false,
  onCouponsMutated,
  refreshKey = 0,
}) => {
  const dispatch = useAppDispatch();
  const [openAssignUser, setOpenAssignUser] = useState(false);
  const { coupons, loading } = useAppSelector((state) => state.coupons);

  const filters = useCouponFilters();
  const { page, setPage, setLimit, hasActiveFilters, query } = filters;

  // Row-scoped action list. "Assign user" is hidden when the coupon already
  // has an assignee — the Assigned column shows (and links to) the buyer, so
  // surfacing a dead-end action would be pure friction.
  const buildDropdownOptions = (coupon: CouponListItem) => {
    const hasAssignee = (coupon.assignedUsers?.length ?? 0) > 0;

    const options: {
      label: string;
      onClick: (c: any) => void;
      returnAll: boolean;
    }[] = assignOnly
      ? []
      : [
          {
            label: "Edit",
            onClick: (c: any) => {
              setSelectedCoupon(c);
              setEditModalOpen(true);
            },
            returnAll: true,
          },
        ];

    if (!hasAssignee) {
      options.push({
        label: "Assign user",
        onClick: (c: any) => {
          setSelectedCoupon(c);
          setOpenAssignUser(true);
        },
        returnAll: true,
      });
    }

    return options;
  };

  const couponData = useMemo(
    () => ({
      columns: [
        { label: "Code", field: "code" },
        { label: "Coupon Type", field: "type" },
        { label: "Value", field: "value" },
        { label: "Created", field: "createdAt" },
        { label: "Expires", field: "expiryDate" },
        { label: "Assigned", field: "assigned" },
        { label: "Status", field: "status" },
        { label: "Action", field: "action" },
      ],
      rows: (coupons?.data?.items ?? []).map(
        (item: CouponListItem, index: number) => {
          const isExpired = new Date(item?.expiryDate) < new Date();
          const value =
            item?.couponAmount !== null && item?.couponAmount !== undefined
              ? `₦${item?.couponAmount}`
              : item?.couponDiscount !== null &&
                item?.couponDiscount !== undefined
              ? `${item?.couponDiscount}%`
              : "N/A";

          return {
            id: index + 1,
            code: item?.couponCode,
            type: prettifyCouponType(item?.couponType as string),
            value,
            assigned: renderAssignedUsers(item?.assignedUsers),
            createdAt: item?.createdAt
              ? moment(item.createdAt).format("Do MMM YYYY")
              : "—",
            expiryDate: moment(item?.expiryDate).format("Do MMM YYYY"),
            status: (
              <span
                className={`text-xs font-semibold px-3 py-1 rounded-full ${
                  isExpired
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {isExpired ? "Expired" : "Active"}
              </span>
            ),
            action: (() => {
              const opts = buildDropdownOptions(item);
              if (opts.length === 0) {
                return <span className="text-gray-400 text-sm">—</span>;
              }
              return (
              <DropdownMenu
                options={opts}
                icon={<MoreVertical style={{ cursor: "pointer" }} />}
                item={item}
              />
              );
            })(),
          };
        }
      ),
    }),
    [coupons, assignOnly]
  );

  useEffect(() => {
    dispatch(fetchAllCoupons(query));
  }, [dispatch, query, refreshKey]);

  return (
    <>
      <CouponFilterBar filters={filters} panelId="coupons-filter-panel" />

      <CustomTable
        data={couponData}
        noRecordText={
          hasActiveFilters
            ? "No coupons match the current filters"
            : "No Coupons Available"
        }
        loading={loading}
        showFooter={true}
        totalPages={coupons?.data?.meta?.totalPages}
        itemsPerPage={coupons?.data?.meta?.itemsPerPage}
        currentPage={page}
        onPageChange={(value: number) => setPage(value)}
        onLimitChange={(value: number) => {
          setLimit(value);
          setPage(0);
        }}
      />

      <AssignCouponModal
        open={openAssignUser}
        onClose={() => setOpenAssignUser(false)}
        onSave={() => {
          setOpenAssignUser(false);
          onCouponsMutated();
        }}
        selectedCoupon={selectedCoupon}
      />
    </>
  );
};

export default CouponsTable;
